import { Switch } from "@/components/Switch.tsx";
import { TutorStatus } from "@/types/tutor.ts";
import { toastAndError } from "@/helpers/toast.ts";
import { useMutation } from "@tanstack/react-query";
import { postTutorStatus, putTutorCapacity } from "@/api/tutors.ts";
import { useEffect, useState } from "react";
import { styled } from "@linaria/react";
import { useUserStore } from "@/stores/useUserStore.ts";
import { None } from "ts-results-es";

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  align-items: end;
`;

const Text = styled.span`
  text-align: right;
  // Disable select as to not steal focus from button
  user-select: none;
`;

export const SwitchNavStatus = () => {
  const [state, setState] = useState(false);
  const { SetStatus, settings, statusUpdateEvent, SetStatusEvent } = useUserStore();
  const updateStatus = useMutation({
    mutationFn: postTutorStatus,
  });

  const updateCapacity = useMutation({
    mutationFn: putTutorCapacity,
  });

  const mutateStatus = (newState: boolean) => {
    const status = newState ? TutorStatus.Available : TutorStatus.Offline;
    updateStatus.mutate(status, {
      onError: (e) => {
        toastAndError("Could not update status", e);
        setState(false);
        SetStatus(TutorStatus.Offline);
      },
      onSuccess: () => {
        SetStatus(status);
      },
    });
  };

  const mutateCapacity = (newCapacity: number) => {
    updateCapacity.mutate(newCapacity, {
      onError: (e) => {
        toastAndError("Could not update status capacity", e);
        setState(false);
        SetStatus(TutorStatus.Offline);
      },
    });
  };

  useEffect(() => {
    if (statusUpdateEvent.isSome()) {
      const newStatus = statusUpdateEvent.unwrap();
      // Safety: Always set's StatusEvent to none, so it's not a loop
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState(newStatus === TutorStatus.Available);
      SetStatus(newStatus);
      SetStatusEvent(None);
    }
  }, [SetStatus, SetStatusEvent, statusUpdateEvent]);

  return (
    <Wrapper
      data-tooltip={"Toggle yourself online to get assigned students"}
      data-direction={"bottom"}
    >
      <Text> I am {state ? "available" : "unavailable"}</Text>
      <Switch
        state={state}
        callback={(newState) => {
          setState(newState);
          mutateStatus(newState);
          mutateCapacity(settings.capacity);
        }}
      />
    </Wrapper>
  );
};
